import React, { useEffect, useState } from 'react';

import { PlusOutlined } from '@ant-design/icons';
import { Card, Tabs, Table, Button, Modal, Form, Input, Switch, message, Divider } from 'antd';
import { useAuth } from '../../contexts/AuthContext';
import SuperAdminAPI from '../../services/SuperAdminAPI';

const { TabPane } = Tabs;

const SystemAdministration = () => {
  // State for modals
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTab, setModalTab] = useState('');
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [featureFlags, setFeatureFlags] = useState([]);
  const [maintenance, setMaintenance] = useState([]);
  const [backups, setBackups] = useState([]);
  const [apiLimits, setApiLimits] = useState([]);
  const [ipAllowlist, setIpAllowlist] = useState([]);
  const [wafRules, setWafRules] = useState([]);
  const [envStatus, setEnvStatus] = useState({});
  const [workerHealth, setWorkerHealth] = useState({ workers: [], queues: {} });
  const [keepWarmEnabled, setKeepWarmEnabled] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);
      const [
        flagsRes,
        maintenanceRes,
        backupsRes,
        apiLimitsRes,
        ipAllowlistRes,
        wafRes,
        envRes,
        workerRes,
        keepWarmRes,
      ] = await Promise.all([
        SuperAdminAPI.listSystemFeatureFlags(),
        SuperAdminAPI.listMaintenance(),
        SuperAdminAPI.listBackups(),
        SuperAdminAPI.listApiLimits(),
        SuperAdminAPI.listIpAllowlist(),
        SuperAdminAPI.listWafRules(),
        SuperAdminAPI.getEnvStatus(),
        SuperAdminAPI.getWorkerHealth(),
        SuperAdminAPI.getKeepWarm(),
      ]);
      setFeatureFlags(flagsRes || []);
      setMaintenance(maintenanceRes || []);
      setBackups(backupsRes || []);
      setApiLimits(apiLimitsRes || []);
      setIpAllowlist(ipAllowlistRes || []);
      setWafRules(wafRes || []);
      setEnvStatus(envRes || {});
      setWorkerHealth(workerRes || { workers: [], queues: {} });
      setKeepWarmEnabled(Boolean(keepWarmRes?.enabled));
    } catch (err) {
      console.error('Failed to load system data:', err);
      message.error('Failed to load system data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const openModal = tab => {
    setModalTab(tab);
    setModalOpen(true);
    form.resetFields();
  };

  const handleKeepWarmToggle = async checked => {
    try {
      setKeepWarmEnabled(checked);
      await SuperAdminAPI.setKeepWarm(checked);
      message.success(`Keep Warm ${checked ? 'enabled' : 'disabled'}`);
    } catch (err) {
      message.error('Failed to update Keep Warm setting');
      setKeepWarmEnabled(!checked);
    }
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      if (modalTab === 'Feature Flag') {
        await SuperAdminAPI.updateSystemFeatureFlag(values);
      } else if (modalTab === 'Maintenance') {
        await SuperAdminAPI.createMaintenance(values);
      } else if (modalTab === 'Backup') {
        await SuperAdminAPI.createBackup(values);
      } else if (modalTab === 'API Limit') {
        await SuperAdminAPI.updateApiLimit({
          ...values,
          limit: values.limit ? Number(values.limit) : undefined,
          windowMs: values.windowMs ? Number(values.windowMs) : undefined,
        });
      } else if (modalTab === 'IP Allowlist') {
        await SuperAdminAPI.updateIpAllowlist({
          ...values,
          ips: String(values.ips || '')
            .split(',')
            .map(v => v.trim())
            .filter(Boolean),
        });
      } else if (modalTab === 'WAF Rule') {
        await SuperAdminAPI.updateWafRule(values);
      }
      message.success(`${modalTab} updated!`);
      setModalOpen(false);
      loadData();
    } catch (err) {
      if (err?.errorFields) return;
      console.error('Failed to update system data:', err);
      message.error('Update failed');
    }
  };

  const { user } = useAuth();
  const isSuperadmin = user?.role === 'super_admin' || user?.systemRole === 'super_admin';

  return (
    <Card title="System Administration">
      <Tabs defaultActiveKey="flags">
        <TabPane tab="Feature Flags" key="flags">
          <Button
            icon={<PlusOutlined />}
            type="primary"
            style={{ marginBottom: 16 }}
            onClick={() => openModal('Feature Flag')}
          >
            Add Feature Flag
          </Button>
          <Table
            columns={[
              { title: 'Key', dataIndex: 'key' },
              {
                title: 'Enabled',
                dataIndex: 'enabled',
                render: v => <Switch checked={v} disabled />,
              },
            ]}
            loading={loading}
            dataSource={featureFlags}
            rowKey={r => r._id || r.id}
            pagination={false}
          />
        </TabPane>
        <TabPane tab="Storage" key="storage">
          <Divider orientation="left">Pluggable Storage Backend</Divider>
          <p style={{ marginBottom: 12 }}>
            Storage migration controls were temporarily disabled in this surface while Phase 1S auth
            and domain split stabilization is in progress.
          </p>
          <Button disabled={!isSuperadmin}>Storage controls temporarily unavailable</Button>
        </TabPane>
        <TabPane tab="Warm-up" key="warmup">
          <Divider orientation="left">Cloud Run Keep Warm</Divider>
          <p style={{ marginBottom: 12 }}>
            When enabled, the platform will periodically ping API and app entrypoints to keep
            HostResolver caches warm and reduce cold-start latency.
          </p>
          <Switch checked={keepWarmEnabled} onChange={handleKeepWarmToggle} />
        </TabPane>
        <TabPane tab="Maintenance" key="maintenance">
          <Button
            icon={<PlusOutlined />}
            type="primary"
            style={{ marginBottom: 16 }}
            onClick={() => openModal('Maintenance')}
          >
            Schedule Maintenance
          </Button>
          <Table
            columns={[
              { title: 'Title', dataIndex: 'title' },
              { title: 'Starts', dataIndex: 'startsAt' },
              { title: 'Ends', dataIndex: 'endsAt' },
              { title: 'Status', dataIndex: 'status' },
            ]}
            loading={loading}
            dataSource={maintenance}
            rowKey={r => r._id || r.id}
            pagination={false}
          />
        </TabPane>
        <TabPane tab="Backups" key="backups">
          <Button
            icon={<PlusOutlined />}
            type="primary"
            style={{ marginBottom: 16 }}
            onClick={() => openModal('Backup')}
          >
            Create Backup
          </Button>
          <Table
            columns={[
              { title: 'Name', dataIndex: 'name' },
              { title: 'Created', dataIndex: 'createdAt' },
              { title: 'Status', dataIndex: 'status' },
            ]}
            loading={loading}
            dataSource={backups}
            rowKey={r => r._id || r.id}
            pagination={false}
          />
        </TabPane>
        <TabPane tab="API Rate Limiting" key="api">
          <Button
            icon={<PlusOutlined />}
            type="primary"
            style={{ marginBottom: 16 }}
            onClick={() => openModal('API Limit')}
          >
            Set API Limit
          </Button>
          <Table
            columns={[
              { title: 'Key', dataIndex: 'key' },
              { title: 'Limit', dataIndex: 'limit' },
              { title: 'Window (ms)', dataIndex: 'windowMs' },
            ]}
            loading={loading}
            dataSource={apiLimits}
            rowKey={r => r._id || r.id}
            pagination={false}
          />
        </TabPane>
        <TabPane tab="IP Allowlist" key="ip">
          <Button
            icon={<PlusOutlined />}
            type="primary"
            style={{ marginBottom: 16 }}
            onClick={() => openModal('IP Allowlist')}
          >
            Add IP Allowlist
          </Button>
          <Table
            columns={[
              { title: 'Name', dataIndex: 'name' },
              { title: 'Scope', dataIndex: 'scope' },
              { title: 'IPs', dataIndex: 'ips', render: ips => (ips || []).join(', ') },
            ]}
            loading={loading}
            dataSource={ipAllowlist}
            rowKey={r => r._id || r.id}
            pagination={false}
          />
        </TabPane>
        <TabPane tab="WAF Config" key="waf">
          <Button
            icon={<PlusOutlined />}
            type="primary"
            style={{ marginBottom: 16 }}
            onClick={() => openModal('WAF Rule')}
          >
            Add WAF Rule
          </Button>
          <Table
            columns={[
              { title: 'Name', dataIndex: 'name' },
              { title: 'Provider', dataIndex: 'provider' },
              { title: 'Rule ID', dataIndex: 'ruleId' },
              { title: 'Enabled', dataIndex: 'enabled', render: v => (v ? 'Yes' : 'No') },
            ]}
            loading={loading}
            dataSource={wafRules}
            rowKey={r => r._id || r.id}
            pagination={false}
          />
        </TabPane>
        <TabPane tab="Env Status" key="env">
          <Table
            columns={[
              { title: 'Key', dataIndex: 'key' },
              { title: 'Configured', dataIndex: 'configured', render: v => (v ? 'Yes' : 'No') },
            ]}
            loading={loading}
            dataSource={Object.entries(envStatus || {}).map(([key, configured]) => ({ key, configured }))}
            rowKey={r => r.key}
            pagination={false}
          />
        </TabPane>
        <TabPane tab="Worker Health" key="workers">
          <Card title="Workers" style={{ marginBottom: 16 }}>
            <Table
              columns={[
                { title: 'Key', dataIndex: 'key' },
                { title: 'Name', dataIndex: 'name' },
                { title: 'Running', dataIndex: 'running', render: v => (v ? 'Yes' : 'No') },
                { title: 'Host', dataIndex: 'host' },
                { title: 'Last Heartbeat', dataIndex: 'lastHeartbeatAt' },
              ]}
              loading={loading}
              dataSource={workerHealth?.workers || []}
              rowKey={r => r.key}
              pagination={false}
            />
          </Card>
          <Card title="Queues">
            <Table
              columns={[
                { title: 'Queue', dataIndex: 'name' },
                { title: 'Waiting', dataIndex: 'waiting' },
                { title: 'Active', dataIndex: 'active' },
                { title: 'Delayed', dataIndex: 'delayed' },
                { title: 'Failed', dataIndex: 'failed' },
              ]}
              loading={loading}
              dataSource={Object.values(workerHealth?.queues || {}).filter(Boolean)}
              rowKey={r => r.name}
              pagination={false}
            />
          </Card>
        </TabPane>
      </Tabs>
      <Modal
        title={`New/Edit ${modalTab}`}
        open={modalOpen}
        onOk={handleOk}
        onCancel={() => setModalOpen(false)}
        okText="Save"
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          {modalTab === 'Feature Flag' && (
            <>
              <Form.Item label="Key" name="key" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item label="Name" name="name">
                <Input />
              </Form.Item>
              <Form.Item label="Description" name="description">
                <Input.TextArea rows={3} />
              </Form.Item>
              <Form.Item label="Enabled" name="enabled" valuePropName="checked">
                <Switch />
              </Form.Item>
            </>
          )}
          {modalTab === 'Maintenance' && (
            <>
              <Form.Item label="Title" name="title" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item label="Starts At" name="startsAt">
                <Input />
              </Form.Item>
              <Form.Item label="Ends At" name="endsAt">
                <Input />
              </Form.Item>
              <Form.Item label="Description" name="description">
                <Input.TextArea rows={3} />
              </Form.Item>
            </>
          )}
          {modalTab === 'Backup' && (
            <Form.Item label="Name" name="name">
              <Input />
            </Form.Item>
          )}
          {modalTab === 'API Limit' && (
            <>
              <Form.Item label="Key" name="key" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item label="Limit" name="limit">
                <Input />
              </Form.Item>
              <Form.Item label="Window (ms)" name="windowMs">
                <Input />
              </Form.Item>
            </>
          )}
          {modalTab === 'IP Allowlist' && (
            <>
              <Form.Item label="Name" name="name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item label="Scope" name="scope">
                <Input />
              </Form.Item>
              <Form.Item label="IPs (comma-separated)" name="ips">
                <Input />
              </Form.Item>
            </>
          )}
          {modalTab === 'WAF Rule' && (
            <>
              <Form.Item label="Name" name="name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item label="Provider" name="provider">
                <Input />
              </Form.Item>
              <Form.Item label="Rule ID" name="ruleId">
                <Input />
              </Form.Item>
              <Form.Item label="Enabled" name="enabled" valuePropName="checked">
                <Switch />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
    </Card>
  );
};

export default SystemAdministration;
